const MAX_LEN = 10_000;

/** Keys whose values are redacted anywhere they appear in a captured payload. */
const REDACT_KEYS = [
  'password',
  'newpassword',
  'currentpassword',
  'token',
  'idtoken',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'cookie',
  'authtoken',
];

const REDACTED = '[REDACTED]';

function redactValue(key: string, value: unknown) {
  return REDACT_KEYS.includes(key.toLowerCase()) ? REDACTED : value;
}

/** Recursively redacts sensitive keys in a plain object/array clone. */
export function redact(input: unknown): unknown {
  if (Array.isArray(input)) {
    return input.map(item => redact(item));
  }
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
      const redacted = redactValue(key, value);
      out[key] = redacted === value ? redact(value) : redacted;
    }
    return out;
  }
  return input;
}

/** Serializes an arbitrary value to a bounded, redacted string. Never throws. */
export function safeStringify(value: unknown): string {
  let text: string;
  try {
    if (typeof value === 'string') {
      text = value;
    } else {
      const seen = new WeakSet();
      text = JSON.stringify(
        redact(value),
        (_key, val) => {
          if (val && typeof val === 'object') {
            if (seen.has(val)) return '[Circular]';
            seen.add(val);
          }
          if (typeof val === 'function') return '[Function]';
          return val;
        },
        2,
      );
    }
  } catch {
    text = String(value);
  }
  if (text == null) return String(text);
  return text.length > MAX_LEN
    ? `${text.slice(0, MAX_LEN)}… [truncated ${text.length - MAX_LEN}]`
    : text;
}

/** Joins console args the way the native console would, each safely stringified. */
export function stringifyArgs(args: unknown[]): string {
  return args.map(arg => safeStringify(arg)).join(' ');
}
